import type { RatingRootCause } from '@zendesk/zqa-services/reviews'
import { api, errorHandlerHook } from '@/api'
import type { FeedbackState } from '@/composables/useReviewSidebar/useReviewSidebar'
import { uploadUnsavedImages } from '@/modules/shared/CommentEditor/plugins/useUnsavedImages'
import type { Dispute } from '../types/dispute'
import { getRequestHeaders } from './index'

export interface DisputeReference {
  connectionId: number
  conversationId: string
  disputeId?: string
}

export interface DisputeCategory {
  id: number
  expectedRating: number | null
  rootCause?: string[] | null
  structuredRootCause?: RatingRootCause
}

interface DisputeCommentPayload {
  comment: Pick<FeedbackState, 'comment' | 'commentTags'>
}

export interface DisputePayload extends DisputeReference {
  data: {
    reviewId: string
    categories?: DisputeCategory[]
    comment: Pick<FeedbackState, 'comment' | 'commentTags'>
    assigneeId?: string
    revieweeId?: string
  }
}

export interface RejectDisputePayload extends DisputeReference {
  data: DisputeCommentPayload
}

export const getDisputes = ({ connectionId, conversationId }: DisputeReference): Promise<{ disputes: Dispute[] }> =>
  api
    .extend({ hooks: { afterResponse: undefined } })
    .get('disputes/ticket', {
      searchParams: { ticketId: conversationId, connectionId },
      headers: getRequestHeaders(),
      hooks: {
        afterResponse: [
          ({ response }) => {
            if (response.status === 404) return new Response('{"disputes": []}')
            return response
          },
          errorHandlerHook,
        ],
      },
    })
    .json()

export const disputeReview = async ({
  connectionId,
  conversationId: ticketId,
  data,
}: DisputePayload): Promise<{ disputes: Dispute[] }> => {
  const response = await api
    .post('disputes', {
      headers: getRequestHeaders(),
      json: { ...data, connectionId, ticketId },
    })
    .json<{ disputes: Dispute[] }>()

  await uploadUnsavedImages(data.comment.comment)

  return response
}

export const disputeReviewee = async ({
  connectionId,
  conversationId: ticketId,
  data,
}: DisputePayload): Promise<{ disputes: Dispute[] }> => {
  const response = await api
    .post('disputes/reviewee', {
      headers: getRequestHeaders(),
      json: { ...data, connectionId, ticketId },
    })
    .json<{ disputes: Dispute[] }>()

  await uploadUnsavedImages(data.comment.comment)

  return response
}

export const rejectDispute = async ({
  connectionId,
  conversationId: ticketId,
  disputeId,
  data,
}: RejectDisputePayload): Promise<{ disputes: Dispute[] }> => {
  const response = await api
    .put(`disputes/${disputeId}/reject`, {
      headers: getRequestHeaders(),
      json: { ...data, connectionId, ticketId },
    })
    .json<{ disputes: Dispute[] }>()

  await uploadUnsavedImages(data.comment.comment)

  return response
}

export const approveReviewDispute = async ({
  connectionId,
  conversationId: ticketId,
  disputeId,
  data,
}: DisputePayload): Promise<{ disputes: Dispute[] }> => {
  const response = await api
    .put(`disputes/${disputeId}/accept`, {
      headers: getRequestHeaders(),
      json: { ...data, connectionId, ticketId },
    })
    .json<{ disputes: Dispute[] }>()

  await uploadUnsavedImages(data.comment.comment)

  return response
}

export const approveRevieweeDispute = async ({
  connectionId,
  conversationId: ticketId,
  disputeId,
  data,
}: DisputePayload): Promise<{ disputes: Dispute[] }> => {
  const response = await api
    .put(`disputes/${disputeId}/reviewee/accept`, {
      headers: getRequestHeaders(),
      json: { ...data, connectionId, ticketId },
    })
    .json<{ disputes: Dispute[] }>()

  await uploadUnsavedImages(data.comment.comment)

  return response
}

export const editDispute = async ({
  connectionId,
  conversationId: ticketId,
  disputeId,
  data,
}: DisputePayload): Promise<{ disputes: Dispute[] }> => {
  const response = await api
    .put(`disputes/${disputeId}`, {
      headers: getRequestHeaders(),
      json: { ...data, connectionId, ticketId },
    })
    .json<{ disputes: Dispute[] }>()

  await uploadUnsavedImages(data.comment.comment)

  return response
}

export const removeDispute = ({
  connectionId,
  conversationId,
  disputeId,
}: DisputeReference): Promise<{ disputes: Dispute[] }> =>
  api
    .delete(`disputes/${disputeId}`, {
      searchParams: { connectionId, ticketId: conversationId },
      headers: getRequestHeaders(),
    })
    .json()

export const markAsViewed = (disputeId: string): Promise<{ disputes: Dispute[] }> =>
  api.post(`disputes/${disputeId}/notifications/mark-viewed`, { headers: getRequestHeaders() }).json()
